'use client';
import { useState, useEffect, useRef, useCallback } from 'react';
import { IEngagementScoreResponse } from '../engagement-typings';

export const useScoreIncrease = (score: IEngagementScoreResponse) => {
  const [showIncrease, setShowIncrease] = useState(false);
  const [increaseAmount, setIncreaseAmount] = useState(0);
  const [animationKey, setAnimationKey] = useState(0);

  const animationCycleRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevScoreRef = useRef(score?.total_engagement_score);
  const prevCreatorRef = useRef(score?.creator_id);

  // Clear any existing timeout when component unmounts or score changes
  const clearAnimationTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    const prevScore = prevScoreRef.current;
    const prevCreator = prevCreatorRef.current;

    if (score?.total_engagement_score && prevScore && score.total_engagement_score > prevScore) {
      if (score?.creator_id === prevCreator) {
        // Clear any existing timeout before starting new animation
        clearAnimationTimeout();

        const newIncreaseAmount = score.total_engagement_score - prevScore;
        setIncreaseAmount(newIncreaseAmount);

        // Force animation to restart by updating the key
        setAnimationKey((prev) => prev + 1);
        animationCycleRef.current = Date.now();

        // Show animation
        setShowIncrease(true);
      }
    }

    // Update refs for next comparison
    prevScoreRef.current = score?.total_engagement_score;
    prevCreatorRef.current = score?.creator_id;

    // Hide animation after 4 seconds
    timeoutRef.current = setTimeout(() => {
      setShowIncrease(false);
    }, 4000);
    // Cleanup function
    return () => {
      clearAnimationTimeout();
    };
  }, [score?.total_engagement_score, score?.creator_id, clearAnimationTimeout]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearAnimationTimeout();
    };
  }, [clearAnimationTimeout]);

  return {
    showIncrease,
    increaseAmount,
    animationCycle: animationCycleRef.current,
    animationKey,
  };
};
